import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#syncPromises();
  }

  /**
   * Copia las promesas de la cinta de anuncios a la franja del pie del cajón,
   * para que el merchant las edite en UN solo lugar.
   *
   * Por qué en JS y no en Liquid: la cinta es OTRA sección (`header-announcements`)
   * y una sección no puede leer los bloques de otra. Las dos están en el mismo
   * HTML, así que el DOM sí puede.
   *
   * El texto del cajón queda escrito a mano en el Liquid como respaldo: si esto
   * no corre, se ven las tres promesas de siempre en vez de un hueco.
   *
   * OJO: el ÍCONO de cada fila lo decide su POSICIÓN, no el texto — la primera
   * fila lleva el de transferencia, la segunda el de tarjeta y la tercera el de
   * envío. Si se reordenan las promesas en el editor, hay que reordenar los
   * íconos en `snippets/header-drawer.liquid`. Y si la cinta trae menos de tres,
   * las filas que sobran se ocultan (necesitan su regla `[hidden]` en el CSS:
   * `.menu-drawer__promise` es `display: flex` y le gana al atributo).
   */
  #syncPromises() {
    const rows = this.querySelectorAll('.menu-drawer__promise');
    if (!rows.length) return;

    // Sólo los slides originales: `announcement-marquee.js` clona copias
    // decorativas para el bucle y las marca `aria-hidden`.
    const source = document.querySelector('.announcement-bar__slides--desktop .announcement-bar__track');
    if (!source) return;

    const texts = Array.from(source.querySelectorAll('.announcement-bar__slide:not([aria-hidden="true"]) .announcement-bar__text'))
      .map((el) => el.textContent?.trim() ?? '')
      .filter(Boolean);

    if (!texts.length) return;

    rows.forEach((row, index) => {
      const slot = row.querySelector('[data-promise-text]');
      if (!slot) return;

      if (index >= texts.length) {
        row.toggleAttribute('hidden', true);
        return;
      }

      row.toggleAttribute('hidden', false);
      const text = texts[index] ?? '';

      // Se escribe con nodos y no con innerHTML: el texto viene de un campo
      // que el merchant edita y podría traer marcado.
      slot.textContent = '';

      // El porcentaje del principio va en naranja, que es la única de las
      // promesas que es señal comercial (DESIGN.md §2). Si no hay porcentaje,
      // no se resalta nada.
      const match = text.match(/^\s*(\d+\s*%(?:\s*off)?)/i);

      if (match) {
        const strong = document.createElement('b');
        strong.textContent = match[1];
        slot.append(strong, text.slice(match[0].length));
      } else {
        slot.textContent = text;
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    // Tambien al abrir, no solo al conectar. Medido: con el sync unicamente en
    // `connectedCallback` el texto volvia al respaldo escrito en el Liquid — el
    // encabezado se rehidrata por Section Rendering API despues de la carga y
    // el markup del servidor pisa lo que el JS habia escrito.
    //
    // Rehacerlo al abrir es ademas el momento correcto: es cuando las promesas
    // se ven, y garantiza que reflejen la cinta aunque el merchant la haya
    // editado en el editor de temas sin recargar. El metodo es idempotente.
    this.#syncPromises();

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
